"use client";

import React from "react";
import { TrendingUp, MapPin, Search, ChartBar as BarChart3, ArrowUpRight, Target, CircleCheck as CheckCircle2, Building2, ChevronRight, Compass } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Separator } from "@/components/ui/separator";
const derbyKeywords = [{
  id: "kw1",
  term: "SEO consultant Derby",
  volume: "880 / mo",
  intent: "High Commercial",
  competition: "Moderate",
  opportunity: "Direct lead generation for local professional services"
}, {
  id: "kw2",
  term: "engineering suppliers Derby",
  volume: "1,400 / mo",
  intent: "B2B Intent",
  competition: "Low",
  opportunity: "Dominance in local manufacturing & industrial queries"
}, {
  id: "kw3",
  term: "commercial solicitors Derby",
  volume: "1,100 / mo",
  intent: "High Intent",
  competition: "High",
  opportunity: "Map Pack schema & geo-targeted landing page capture"
}, {
  id: "kw4",
  term: "IT support companies Derby",
  volume: "950 / mo",
  intent: "Commercial",
  competition: "Medium",
  opportunity: "Local technical SEO & citation optimization"
}];
const trendData = [{
  month: "Jan",
  derbyVolume: 12400,
  mapPackClicks: 4100
}, {
  month: "Feb",
  derbyVolume: 13200,
  mapPackClicks: 4500
}, {
  month: "Mar",
  derbyVolume: 14100,
  mapPackClicks: 4900
}, {
  month: "Apr",
  derbyVolume: 15300,
  mapPackClicks: 5400
}, {
  month: "May",
  derbyVolume: 16800,
  mapPackClicks: 6100
}, {
  month: "Jun",
  derbyVolume: 18200,
  mapPackClicks: 6800
}];
const competitorInsights = [{
  id: "comp1",
  title: "Google 3-Pack Schema Deficit",
  desc: "Over 68% of local Derby buyers click directly inside the Google Map Pack, yet less than 20% of local businesses maintain structured local business schema."
}, {
  id: "comp2",
  title: "Thin Regional Content Shortfall",
  desc: "Most regional competitors rely on 300-word generic pages. I build deep, localized 1,500+ word resource hubs that predictably outrank them."
}, {
  id: "comp3",
  title: "East Midlands Authority Gap",
  desc: "Interlinking your Derby business with regional hubs like Nottingham and Leicester builds powerful entity trust in Google's local algorithms."
}];
const neighboringCities = [{
  id: "city1",
  name: "Nottingham Local SEO",
  distance: "15 miles east",
  description: "Expand your footprint into Nottinghamshire with tailored B2B, tech, and service keyword positioning.",
  path: "/LocalSEOPage",
  badge: "East Midlands Hub"
}, {
  id: "city2",
  name: "Leicester Local SEO",
  distance: "28 miles south-east",
  description: "Target high-intent manufacturing, retail, and logistics search clusters across Leicestershire.",
  path: "/LocalSEOPage",
  badge: "East Midlands Hub"
}, {
  id: "city3",
  name: "Birmingham Local SEO",
  distance: "40 miles south-west",
  description: "Capture West Midlands enterprise queries and establish regional market leadership.",
  path: "/LocalSEOPage",
  badge: "West Midlands Hub"
}];
const chartConfig = {
  derbyVolume: {
    label: "Derby Search Demand",
    color: "hsl(220 80% 30%)"
  },
  mapPackClicks: {
    label: "Google Map Pack Clicks",
    color: "hsl(340 75% 65%)"
  }
};
export default function Localsearchtrends() {
  return <section id="localsearchtrendsanalysis" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        {/* Section Header */}
        <div className="max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-medium">
              <MapPin className="size-3 text-primary" />
              Derby & East Midlands Search Intelligence
            </Badge>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-default">
            Derby Local Search Trends & Market Opportunities
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-default">
            I systematically track search intent and competitive gaps across Derby and Derbyshire. When you work with me, you get hands-on analysis of local keyword volume and direct strategy to turn regional search demand into active sales inquiries.
          </p>
        </div>

        {/* Charts & Key Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-8 flex flex-col justify-between">
            <Card className="h-full border border-border bg-card shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <CardTitle className="text-xl font-semibold text-card-foreground font-default flex items-center gap-2">
                      <TrendingUp className="size-5 text-primary" />
                      Derby Local Search Demand Growth
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground">
                      Monthly search query expansion vs local map pack engagement across Derby business sectors.
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="w-fit text-xs border-primary/30 text-primary">
                    Updated Local Data
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <ChartContainer config={chartConfig} className="h-64 sm:h-72 w-full">
                  <AreaChart data={trendData} margin={{
                  top: 12,
                  right: 12,
                  left: 0,
                  bottom: 0
                }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border" />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} className="text-xs text-muted-foreground" />
                    <YAxis tickLine={false} axisLine={false} className="text-xs text-muted-foreground" tickFormatter={val => `${val / 1000}k`} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Area type="monotone" dataKey="derbyVolume" stroke="var(--color-derbyVolume)" fill="var(--color-derbyVolume)" fillOpacity={0.12} strokeWidth={2.5} />
                    <Area type="monotone" dataKey="mapPackClicks" stroke="var(--color-mapPackClicks)" fill="var(--color-mapPackClicks)" fillOpacity={0.2} strokeWidth={2.5} />
                  </AreaChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-4 flex flex-col gap-4">
            <Card className="border border-border bg-card shadow-sm flex-1 flex flex-col justify-center">
              <CardContent className="p-6 space-y-6">
                <div className="space-y-1">
                  <span className="text-3xl sm:text-4xl font-bold text-primary tracking-tight font-default">
                    68%
                  </span>
                  <p className="text-sm font-medium text-card-foreground">Map Pack Intent Ratio</p>
                  <p className="text-xs text-muted-foreground">
                    Derby local service searches converting via Google 3-Pack listings.
                  </p>
                </div>

                <Separator />

                <div className="space-y-1">
                  <span className="text-3xl sm:text-4xl font-bold text-accent tracking-tight font-default">
                    +34%
                  </span>
                  <p className="text-sm font-medium text-card-foreground">Commercial Query Growth</p>
                  <p className="text-xs text-muted-foreground">
                    Year-over-year rise in localized B2B & industrial search volume in Derby.
                  </p>
                </div>

                <Separator />

                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary" />
                    <span className="text-sm font-medium text-card-foreground">Direct Consultant Access</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    I personally craft and execute your strategy—no agency account managers.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Local Keywords Showcase */}
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-foreground font-default flex items-center gap-2">
                <Search className="size-5 text-primary" />
                Derby High-Intent Search Clusters
              </h3>
              <p className="text-sm text-muted-foreground">
                Representative keyword clusters driving qualified leads in Derby and surrounding business parks.
              </p>
            </div>
            <Link to="/LocalSEOPage">
              <Button variant="outline" size="sm" className="gap-2">
                <span>View All Local SEO Services</span>
                <ArrowUpRight className="size-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {derbyKeywords.map((kw, i) => <div data-index={i} key={kw.id}>
                <Card className="border border-border bg-card shadow-xs hover:shadow-md transition-shadow h-full flex flex-col justify-between">
                  <CardHeader className="p-5 pb-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <Badge variant="secondary" className="text-[13px] font-normal">
                        {kw.intent}
                      </Badge>
                      <span className="text-xs font-mono font-medium text-muted-foreground">
                        {kw.volume}
                      </span>
                    </div>
                    <CardTitle className="text-base font-semibold text-card-foreground font-default leading-snug">
                      "{kw.term}"
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-5 pt-0 space-y-3">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Competition:</span>
                      <span className="font-medium text-card-foreground">{kw.competition}</span>
                    </div>
                    <Separator />
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {kw.opportunity}
                    </p>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>

        {/* Competitor Opportunity Insights */}
        <div className="space-y-6">
          <div className="max-w-2xl">
            <h3 className="text-xl sm:text-2xl font-bold text-foreground font-default flex items-center gap-2">
              <BarChart3 className="size-5 text-primary" />
              Derby Local Competition Breakdown
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Where regional competitors are falling behind and how I position your business to win local market share.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {competitorInsights.map((insight, i) => <div data-index={i} key={insight.id}>
                <Card className="border border-border bg-card shadow-xs h-full flex flex-col justify-between">
                  <CardHeader className="p-6">
                    <div className="p-2.5 w-fit rounded-lg bg-muted text-primary mb-3">
                      <Target className="size-5" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground font-default">
                      {insight.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 pt-0">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {insight.desc}
                    </p>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>

        {/* Geographic Hub Context */}
        <div className="rounded-2xl bg-muted/60 border border-border p-6 sm:p-8 space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-1 max-w-2xl">
              <div className="flex items-center gap-2">
                <Compass className="size-5 text-primary" />
                <h3 className="text-lg sm:text-xl font-bold text-foreground font-default">
                  Geographic Coverage Across Neighboring Midlands Cities
                </h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Local search authority grows exponentially when you connect connected markets. I help Derby businesses build unified SEO strategies across neighboring commercial centers.
              </p>
            </div>
            <Link to="/LocalSEOPage">
              <Button variant="default" size="sm" className="gap-2 shrink-0">
                <span>Explore Local SEO Framework</span>
                <ChevronRight className="size-4" />
              </Button>
            </Link>
          </div>

          <Separator className="bg-border/80" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {neighboringCities.map((city, i) => <div data-index={i} key={city.id}>
                <Card className="border border-border bg-background shadow-xs hover:border-primary/40 transition-colors h-full flex flex-col justify-between">
                  <CardHeader className="p-5 pb-2">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <Badge variant="outline" className="text-[12px]">
                        {city.badge}
                      </Badge>
                      <span className="text-[13px] text-muted-foreground">{city.distance}</span>
                    </div>
                    <CardTitle className="text-base font-semibold text-foreground font-default">
                      {city.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-5 pt-0 space-y-4">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {city.description}
                    </p>
                    <Link to={city.path} className="inline-block">
                      <Button variant="ghost" size="sm" className="h-8 px-0 text-primary hover:bg-transparent hover:text-primary/80 gap-1 text-xs">
                        <span>Read Regional Strategy</span>
                        <ArrowUpRight className="size-3" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>

        {/* Direct CTA Block */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 p-6 sm:p-8 rounded-xl bg-card border border-border shadow-xs">
          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_derby_consultant.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_derby_consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_derby_consultant.png-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant for Derby" className="size-14 rounded-full object-cover border-2 border-primary/20" loading="lazy" />
              <span className="absolute bottom-0 right-0 size-3.5 rounded-full bg-emerald-500 border-2 border-background" />
            </div>
            <div className="space-y-1">
              <h4 className="text-base font-semibold text-card-foreground font-default">
                Want a Custom Local Search Audit for Your Derby Business?
              </h4>
              <p className="text-xs sm:text-sm text-muted-foreground">
                I will personally analyze your current Derby search rankings, Google Business profile, and local competitor gaps.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto shrink-0">
            <Link to="/ContactPage" className="w-full sm:w-auto">
              <Button variant="default" size="default" className="w-full sm:w-auto gap-2">
                <span>Book a Free SEO Consultation</span>
                <ChevronRight className="size-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}